import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiPlus, FiArrowRight, FiX } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import "../styles/Dashboard.css";
import "../styles/Modal.css";

const BASE_DOMAIN = (process.env.REACT_APP_BASE_URL || "https://linksquire.com").replace(/\/+$/, "");

export default function AdvancedABTestingPage() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const navigate = useNavigate();

  const fetchLinks = useCallback(async () => {
    try {
      // For now, fetch all links and filter. In a larger app, we'd add an API query for ab_testing_enabled=true
      const data = await api.getLinks(1, 100);
      const abLinks = data.links.filter(l => l.ab_testing_enabled);
      setLinks(abLinks);
    } catch (err) {
      console.error("Failed to fetch A/B test links", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = "A/B Testing - LinkSquire";
    fetchLinks();
  }, [fetchLinks]);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Advanced</h1>
        <button className="btn-primary" onClick={() => setShowAddModal(true)}>
          <FiPlus /> Add A/B Test
        </button>
      </div>

      {/* Advanced sub-nav */}
      <div className="advanced-tabs" style={{ display: 'flex', gap: '20px', marginBottom: '24px', borderBottom: '1px solid var(--border)' }}>
        <Link to="/advanced/ab-testing" style={{ padding: '8px 4px', borderBottom: '2px solid var(--text)', color: 'var(--text)', fontWeight: 500, textDecoration: 'none' }}>
          A/B Testing
        </Link>
        <Link to="/advanced/geo-targeting" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>
          Geo-Targeting
        </Link>
        <Link to="/advanced/pixels" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>
          Retargeting Pixels
        </Link>
        <Link to="/advanced/domains" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>
          Custom Domains
        </Link>
        <Link to="/advanced/conversions" style={{ padding: '8px 4px', color: 'var(--text-secondary)', fontWeight: 500, textDecoration: 'none' }}>
          Conversions
        </Link>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading...</p>
        </div>
      ) : links.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', background: 'var(--surface)' }}>
          <h3 style={{ marginBottom: '8px', fontSize: '16px' }}>No A/B Tests active</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px', fontSize: '14px' }}>Route traffic to multiple destinations to see what converts best.</p>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>Create your first A/B test</button>
        </div>
      ) : (
        <div className="links-section">
          <div className="links-header">
            <h2>Active A/B Tests</h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {links.map(link => {
              let destCount = 0;
              try { destCount = JSON.parse(link.ab_destinations || "[]").length; } catch {}
              return (
                <div key={link.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', borderBottom: '1px solid var(--border)', background: '#fff' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px' }}>{link.title || link.short_code}</div>
                    <a href={link.short_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontSize: '13px', textDecoration: 'none' }}>{link.short_url}</a>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Routing to {destCount} destination{destCount !== 1 ? 's' : ''} • {link.click_count} total clicks
                    </div>
                  </div>
                  <button className="btn-sm" onClick={() => navigate(`/advanced/ab-testing/${link.id}`)}>
                    Configure <FiArrowRight />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showAddModal && (
        <AddABTestModal onClose={() => setShowAddModal(false)} onAdded={(id) => navigate(`/advanced/ab-testing/${id}`)} />
      )}
    </div>
  );
}

function AddABTestModal({ onClose, onAdded }) {
  const [tab, setTab] = useState('existing'); // 'existing' or 'new'
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const { addToast } = useToast();

  // New link state
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [customCode, setCustomCode] = useState("");

  useEffect(() => {
    if (tab === 'existing') {
      setLoading(true);
      api.getLinks(1, 100).then(data => {
        // Filter out links that already have AB Testing OR Geo Targeting enabled
        setLinks(data.links.filter(l => !l.ab_testing_enabled && !l.geo_targeting_enabled));
      }).catch(() => addToast("Failed to fetch links", "error"))
        .finally(() => setLoading(false));
    }
  }, [tab, addToast]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (tab === 'existing') {
        if (!selectedId) throw new Error("Please select a link");
        await api.updateLink(selectedId, { ab_testing_enabled: true });
        onAdded(selectedId);
      } else {
        if (!url) throw new Error("Destination URL is required");
        const body = { original_url: url, ab_testing_enabled: true };
        if (title.trim()) body.title = title.trim();
        if (customCode.trim()) body.custom_code = customCode.trim();
        const res = await api.createLink(body);
        onAdded(res.id);
      }
    } catch (err) {
      addToast(err.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add A/B Test</h2>
          <button className="modal-close" onClick={onClose}><FiX /></button>
        </div>
        
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', background: 'var(--surface-hover)', padding: '4px', borderRadius: 'var(--radius)' }}>
          <button 
            style={{ flex: 1, padding: '6px', border: 'none', background: tab === 'existing' ? '#fff' : 'transparent', borderRadius: '4px', boxShadow: tab === 'existing' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none', cursor: 'pointer', fontWeight: tab === 'existing' ? 500 : 400 }}
            onClick={() => setTab('existing')}
          >Select Existing</button>
          <button 
            style={{ flex: 1, padding: '6px', border: 'none', background: tab === 'new' ? '#fff' : 'transparent', borderRadius: '4px', boxShadow: tab === 'new' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none', cursor: 'pointer', fontWeight: tab === 'new' ? 500 : 400 }}
            onClick={() => setTab('new')}
          >Create New Link</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {tab === 'existing' ? (
            <div className="form-group">
              <label>Select a link</label>
              <select className="form-select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} required>
                <option value="" disabled>-- Choose link --</option>
                {links.map(l => (
                  <option key={l.id} value={l.id}>{l.title || l.short_code} ({l.original_url.substring(0, 30)}...)</option>
                ))}
              </select>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label>Primary Destination URL *</label>
                <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/a" required />
              </div>
              <div className="form-group">
                <label>Title (optional)</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="My A/B Test" />
              </div>
              <div className="form-group">
                <label>Custom Link</label>
                <div className="code-input-group">
                  <span className="code-prefix">{BASE_DOMAIN}/</span>
                  <input type="text" value={customCode} onChange={(e) => setCustomCode(e.target.value)} placeholder="Leave empty to auto-generate" />
                </div>
              </div>
            </>
          )}
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? "Processing..." : "Continue to Configuration"}
          </button>
        </form>
      </div>
    </div>
  );
}
